<template>
  <div class="d-flex flex-column h-100">
    <TableCardLayout>
      <template #table-controls>
        <TableControls :loading="pending">
          <template #left>
            <div class="d-flex flex-wrap align-center gap-2 mr-auto">
              <div class="d-flex gap-1 align-center">
                <b-form-select
                  id="toxprint-profile-select"
                  v-model="profile"
                  :options="toxprintsProfilesForSelect"
                  value-field="id"
                  text-field="name"
                  style="min-width: 12rem; max-width: 18rem"
                />
                <v-tooltip
                  top
                  v-bind="defaultVTooltipProps"
                  activator="#toxprint-profile-select"
                >
                  ToxPrint profile
                </v-tooltip>

                <ToxprintsSettings
                  :settings="toxprintsSettings"
                  button-title="mdi-cog-outline"
                  title="ChemoTypes enrichment settings"
                  @save="onSaveSettings"
                />
              </div>
              <ReportControls
                :status="reportControlsStatus"
                calculate-id="toxprints-generate-button"
                clear-id="toxprints-clear-button"
                calculate-tooltip="Generate ToxPrints from ChemCart"
                calculate-aria-label="Generate ToxPrints from ChemCart"
                clear-tooltip="Clear generated ToxPrints"
                clear-aria-label="Clear generated ToxPrints"
                :ui="{ calculate: 'primary' }"
                @calculate="onGenerateToxprints"
                @clear="onClearReportControls"
              />
            </div>
          </template>

          <template #right>
            <AssayViewModeToggle
              v-if="isAssayMode && embeddedAssayView"
              :assay-view="embeddedAssayView"
            />

            <v-btn-toggle
              v-if="isAssaysMode && embeddedAssaysTable && embeddedAssaysTable.assays.length"
              v-model="embeddedAssaysTable.view"
              dense
              class="mr-2"
            >
              <v-btn
                title="Assays view"
                v-b-tooltip
                aria-label="Assays view"
              >
                <v-icon>mdi-table-column-plus-after</v-icon>
              </v-btn>
              <v-btn
                title="Assay enrichments view"
                v-b-tooltip
                aria-label="Assay enrichments view"
              >
                <v-icon>mdi-table-column-plus-before</v-icon>
              </v-btn>
            </v-btn-toggle>

            <ToxprintsMoreMenu
              :labels="toxprintsLabels"
              @stored-assays="navigateIfNeeded('/toxprints/assays')"
              @explore-global="navigateIfNeeded('/toxprints/global_assays')"
            />
            <AssayViewControls
              v-if="isAssayMode && embeddedAssayView"
              :assay-view="{
                chemicalsCount: embeddedAssayView.chemicals ? embeddedAssayView.chemicals.length : 0,
                view: embeddedAssayView.view,
                hasActivities: embeddedAssayView.hasActivities,
                hasEnriched: embeddedAssayView.hasEnriched,
                showOtherMetrics: embeddedAssayView.showOtherMetrics,
                showEnrichedOnly: embeddedAssayView.showEnrichedOnly,
                showStructures: embeddedAssayView.showStructures,
                showEmptyRows: !embeddedAssayView.hideChemicalsWithNoToxprints,
                showNames: embeddedAssayView.showNames,
                showToxprintStructures: embeddedAssayView.showToxprintStructures,
                showImages: embeddedAssayView.showImages,
                numberOfEmptyChemicals: embeddedAssayView.numberOfEmptyChemicals,
                pending: embeddedAssayView.pending,
                downloading: embeddedAssayView.downloading,
                isAuthenticated: embeddedAssayView.isAuthenticated,
                toxprintsCategories: embeddedAssayView.toxprintsCategories,
                assay: embeddedAssayView.assay,
                name: embeddedAssayView.name,
                toxprintsUrl: embeddedAssayView.TOXPRINTS_URL,
                canManageAcl: Boolean(embeddedAssayView.assay.id && embeddedAssayView.canACL(embeddedAssayView.assay)),
                canDelete: embeddedAssayView.canDelete(embeddedAssayView.assay),
              }"
              @update:show-other-metrics="embeddedAssayView.showOtherMetrics = $event"
              @update:show-enriched-only="embeddedAssayView.showEnrichedOnly = $event"
              @update:show-structures="embeddedAssayView.showStructures = $event"
              @update:show-empty-rows="embeddedAssayView.hideChemicalsWithNoToxprints = !$event"
              @update:show-names="embeddedAssayView.showNames = $event"
              @update:show-toxprint-structures="embeddedAssayView.showToxprintStructures = $event"
              @update:show-images="embeddedAssayView.showImages = $event"
              @export="embeddedAssayView.exportFile($event)"
              @assay-created="embeddedAssayView.onAssayCreated($event)"
              @assay-updated="embeddedAssayView.onAssayUpdated($event)"
              @acl-updated="embeddedAssayView.onAclUpdated($event)"
              @reset="embeddedAssayView.onReset()"
            />

            <AssaysTableControls
              v-if="isAssaysMode && embeddedAssaysTable"
              :assays-table="{
                pending: embeddedAssaysTable.pending,
                downloading: embeddedAssaysTable.downloading,
                assaysCount: embeddedAssaysTable.assays.length,
                total: embeddedAssaysTable.total,
                view: embeddedAssaysTable.view,
                category: embeddedAssaysTable.category,
                label: embeddedAssaysTable.label,
                global: embeddedAssaysTable.global,
                showBrief: embeddedAssaysTable.showBrief,
                showTable: embeddedAssaysTable.showTable,
                showLabels: embeddedAssaysTable.showLabels,
                showDetails: embeddedAssaysTable.showDetails,
              }"
              @update:category="embeddedAssaysTable.category = $event"
              @update:label="embeddedAssaysTable.label = $event"
              @update:show-brief="embeddedAssaysTable.showBrief = $event"
              @update:show-table="embeddedAssaysTable.showTable = $event"
              @update:show-labels="embeddedAssaysTable.showLabels = $event"
              @update:show-details="embeddedAssaysTable.showDetails = $event"
              @export="embeddedAssaysTable.exportFile($event)"
            />
          </template>
        </TableControls>
      </template>

      <AssayView
        v-if="isAssayMode"
        embedded
        @controller-ready="embeddedAssayView = $event"
      />

      <AssaysTable
        v-else-if="isAssaysMode"
        embedded
        @controller-ready="embeddedAssaysTable = $event"
      />
    </TableCardLayout>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { SERVICES_URL, TOXPRINTS_URL, getErrorMessage } from '@/main'
import ReportControls from '@/components/ReportControls.vue'
import ToxprintsSettings from '@/components/toxprints/ToxprintsSettings'
import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import TableControls from '@/components/report-page-layouts/TableControls.vue'
import AssayView from '@/components/toxprints/AssayView'
import AssaysTable from '@/components/toxprints/AssaysTable'
import AssayViewControls from '@/components/toxprints/AssayViewControls.vue'
import AssayViewModeToggle from '@/components/toxprints/AssayViewModeToggle.vue'
import AssaysTableControls from '@/components/toxprints/AssaysTableControls.vue'
import ToxprintsMoreMenu from '@/components/toxprints/ToxprintsMoreMenu.vue'
import { TOXPRINTS_ROUTE_MODES, getToxprintsRouteState } from '@/components/toxprints/toxprintsRouteState'
import {
  toxprintsSelectedProfilePreference,
  toxprintsSettingsPreference,
} from '@/components/toxprints/toxprintsPreferences'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { showErrorToast } from '@/utils/toast'

export default {
  components: {
    TableCardLayout,
    TableControls,
    ReportControls,
    AssayView,
    AssaysTable,
    AssayViewControls,
    AssayViewModeToggle,
    AssaysTableControls,
    ToxprintsMoreMenu,
    ToxprintsSettings,
  },

  data() {
    return {
      defaultVTooltipProps,
      profile: null,
      pending: false,
      profileLoading: false,
      embeddedAssayView: null,
      embeddedAssaysTable: null,
    }
  },

  computed: {
    ...mapGetters([
      'getCheckedStructures',
      'toxprintsLabels',
      'toxprintsResult',
      'toxprintsProfiles',
      'toxprintsDefaultProfile',
    ]),
    toxprintsSettings() {
      return toxprintsSettingsPreference.value
    },
    toxprintsSelectedProfile() {
      return toxprintsSelectedProfilePreference.value
    },
    reportControlsStatus() {
      if (this.pending) {
        return 'loading'
      }

      if (this.hasLastReport) {
        return this.profile && this.getCheckedStructures?.length ? 'stale' : 'fresh'
      }

      return this.profile && this.getCheckedStructures?.length ? 'ready' : 'invalid'
    },
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    SERVICES_URL() {
      return SERVICES_URL
    },
    toxprintsProfilesForSelect() {
      return this.toxprintsProfiles.map((profile) => {
        return {
          id: profile.name,
          name: profile.description,
          description: profile.detailedDescription,
        }
      })
    },
    routeState() {
      return getToxprintsRouteState(this.$route)
    },
    hasLastReport() {
      return Boolean(this.toxprintsResult?.chemicals?.length || this.toxprintsResult?.metrics?.length)
    },
    isAssayMode() {
      return [TOXPRINTS_ROUTE_MODES.REPORT, TOXPRINTS_ROUTE_MODES.ASSAY_DETAIL].includes(this.routeState.mode)
    },
    isAssaysMode() {
      return [TOXPRINTS_ROUTE_MODES.ASSAYS, TOXPRINTS_ROUTE_MODES.GLOBAL_ASSAYS].includes(this.routeState.mode)
    },
  },

  watch: {
    'profile': {
      handler: function (val) {
        toxprintsSelectedProfilePreference.value = val
      },
      deep: true,
    },
    'toxprintsProfiles'() {
      this.ensureDefaultProfile()
    },
    'toxprintsDefaultProfile'() {
      this.ensureDefaultProfile()
    },
    '$route.fullPath'() {
      this.syncReportRoute()
    },
    'hasLastReport'() {
      this.syncReportRoute()
    },
  },

  methods: {
    navigateIfNeeded(location) {
      const target = this.$router.resolve(location).route.fullPath
      if (target !== this.$route.fullPath) {
        this.$router.push(location)
      }
    },
    syncReportRoute() {
      if (this.routeState.mode === TOXPRINTS_ROUTE_MODES.HOME && this.hasLastReport) {
        this.navigateIfNeeded('/toxprints/report')
        return
      }

      if (this.routeState.mode === TOXPRINTS_ROUTE_MODES.REPORT && !this.hasLastReport) {
        this.navigateIfNeeded('/toxprints')
      }
    },
    ensureDefaultProfile() {
      if (this.profile || !this.toxprintsProfiles.length || !this.toxprintsDefaultProfile) return

      this.profile = this.toxprintsProfiles.find((p) => p.name === this.toxprintsDefaultProfile)?.name || null
    },
    maybeOpenChemCart() {
      const noChecked = !this.getCheckedStructures?.length
      const noResults = !this.hasLastReport
      if (noChecked && noResults) this.$store.commit('SET_VIEW_STRUCTURES', true)
    },
    onClearReportControls() {
      this.$store.commit('resetToxprintsResult')
      if (this.$route.path !== '/toxprints') {
        this.navigateIfNeeded('/toxprints')
      }
    },
    onSaveSettings(settings) {
      toxprintsSettingsPreference.value = { ...settings }
    },
    onFileUploaded(result) {
      this.pending = false
      this.$store.commit('setToxprintsResult', result)
      this.navigateIfNeeded('/toxprints/report')
    },
    onGenerateToxprints(chemicals) {
      this.$store.commit('SET_VIEW_STRUCTURES', false)

      this.pending = true

      this.$store
        .dispatch('calculateToxprints', {
          chemicals: Array.isArray(chemicals) ? chemicals : this.getCheckedStructures,
          options: Object.assign({}, this.toxprintsSettings, {
            profile: this.profile,
          }),
        })
        .then(() => {
          this.pending = false
          this.navigateIfNeeded('/toxprints/report')
        })
        .catch((error) => {
          showErrorToast(error)
        })
        .finally(() => {
          this.pending = false
        })
    },
  },

  created() {
    this.maybeOpenChemCart()
  },

  mounted() {
    this.profileLoading = true
    this.profile = this.toxprintsSelectedProfile
    this.ensureDefaultProfile()
    this.profileLoading = false
    this.syncReportRoute()
  },
}
</script>

<style scoped>
.toxprints-home-card {
  max-width: 36rem;
}
</style>
